import { type ClassValue, clsx } from "clsx";
import { env } from "~/env.mjs";

import { twMerge } from "tailwind-merge";
import { format } from "date-fns";
import { srLatn } from "date-fns/locale";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function composeEventHandlers<E>(
  originalEventHandler?: (event: E) => void,
  ourEventHandler?: (event: E) => void,
  { checkForDefaultPrevented = true } = {},
) {
  return function handleEvent(event: E) {
    originalEventHandler?.(event);

    if (
      checkForDefaultPrevented === false ||
      !(event as unknown as Event).defaultPrevented
    ) {
      return ourEventHandler?.(event);
    }
  };
}

export function formatDate(date: Date | string | number) {
  return format(new Date(date), "dd.MM.yyyy", {
    locale: srLatn,
  });
}

export function assertNever(x: never): never {
  throw new Error(`Unexpected object: ${JSON.stringify(x)}`);
}

export const currency = "€";

export function absoluteUrl(path: string) {
  const isFullyQualifiedUrl = /^https?:\/\//i.test(path);
  const normalizedBase = env.NEXT_PUBLIC_DEPLOYMENT_URL.endsWith("/")
    ? env.NEXT_PUBLIC_DEPLOYMENT_URL.slice(0, -1)
    : env.NEXT_PUBLIC_DEPLOYMENT_URL;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = isFullyQualifiedUrl ? path : `${normalizedBase}${normalizedPath}`;

  if (url.includes("resimoprobleme.com") && !url.includes("www.")) {
    return url.replace("https://", "https://www.");
  }

  return url;
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: "accurate" | "normal";
  } = {},
) {
  const { decimals = 0, sizeType = "normal" } = opts;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === "accurate"
      ? (accurateSizes[i] ?? "Bytest")
      : (sizes[i] ?? "Bytes")
  }`;
}

export function formatFileSize(bytes: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${Math.round(size * 100) / 100} ${units[unitIndex]}`;
}

/**
 * Converts markdown to plain text by removing markdown syntax
 * @param markdown The markdown text to convert
 * @returns Plain text with markdown syntax removed
 */
export function markdownToText(markdown: string): string {
  if (!markdown) return "";

  // Remove headers
  markdown = markdown.replace(/^#+\s+/gm, "");

  // Remove bold and italic
  markdown = markdown.replace(/(\*\*|__)(.*?)\1/g, "$2");
  markdown = markdown.replace(/(\*|_)(.*?)\1/g, "$2");

  // Remove links
  markdown = markdown.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");

  // Remove images
  markdown = markdown.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, "");

  // Remove blockquotes
  markdown = markdown.replace(/^\s*>\s*/gm, "");

  // Remove code blocks
  markdown = markdown.replace(/```[\s\S]*?```/g, "");
  markdown = markdown.replace(/`([^`]+)`/g, "$1");

  // Remove horizontal rules
  markdown = markdown.replace(/^\s*[-*_]{3,}\s*$/gm, "");

  // Remove lists
  markdown = markdown.replace(/^\s*[-*+]\s+/gm, "");
  markdown = markdown.replace(/^\s*\d+\.\s+/gm, "");

  // Remove multiple newlines
  markdown = markdown.replace(/\n{3,}/g, "\n\n");

  return markdown.trim();
}
